import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { usePreferenceScope } from "@/hooks/useStoredPreference";

export const useRecordDialog = <T extends { id: number }>(resource: string) => {
    const [params, setParams] = useSearchParams();
    const scope = usePreferenceScope();
    const id = Number(params.get("record")) || null;
    const client = useQueryClient();
    const key = ["record", resource, scope, id];
    const record = useQuery({
        queryKey: key,
        enabled: id !== null && id > 0,
        retry: false,
        queryFn: async ({ signal }): Promise<T> =>
            (await api.get<{ data: T }>(`/${resource}/${id}`, { signal })).data
                .data,
    });
    const select = (item: T | null): void => {
        const next = new URLSearchParams(params);
        if (item) {
            client.setQueryData(["record", resource, scope, item.id], item);
            next.set("record", String(item.id));
        } else {
            next.delete("record");
        }
        if (next.toString() !== params.toString()) {
            setParams(next, { preventScrollReset: true });
        }
    };
    return {
        item: id ? (record.data ?? null) : null,
        select,
        error: record.error instanceof Error ? record.error.message : null,
        reload: (): void => {
            if (id) {
                void record.refetch();
            }
        },
    };
};
